import { HttpClient } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { collection, doc, docData, DocumentData, Firestore, getDoc, getDocFromServer, onSnapshot, query, setDoc, where } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { ActiveStatus } from "@interfaces/subscription";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { BehaviorSubject, EMPTY, firstValueFrom, from, lastValueFrom, Observable, of, Subject, throwError } from "rxjs";
import { catchError, filter, map, retry, shareReplay, startWith, switchMap, take } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { ClarityService } from "src/app/services/clarity.service";
import { CommonService } from "src/app/services/common/common.service";
import { GameBalanceService } from "src/app/services/game-balance.service";
import { OneSignalService } from "src/app/services/one-signal.service";
import { RequestPaymentService } from "src/app/services/RequestBodyService/RequestPayment/request-payment.service";
import { RequestProfileService } from "src/app/services/RequestBodyService/RequestProfile/request-profile.service";
import { RequestUserService } from "src/app/services/RequestBodyService/RequestUser/request-user.service";
import { UserManagementService } from "src/app/services/user-management.service";
import { StoreRepositoryService } from "../store/store-repository.service";
import { StoreJackpotTriggerDataService } from "../store/services/store-jackpot-trigger-data.service";
import { SyndicateRepositoryService } from "../syndicate/syndicate-repository.service";
import { WalletService } from "./account-wallet/wallet.services";
import { LoggingService } from "src/app/services/logging.service";
@Injectable({
  providedIn: "root",
})
export class AccountRepositoryService {
  phoneNumberUpdate$ = new BehaviorSubject({ dialCode: "", number: "", iso2: "" });
  selectedLanguage: string = CommonVariable?.preferredLanguage;
  private dismissAllSubject = new Subject<string>();
  public dismissAll$ = this.dismissAllSubject.asObservable();
  syndicatePromotion$ = new BehaviorSubject(false);
  constructor(
    private firestore: Firestore,
    private http: HttpClient,
    private authService: AuthService,
    private storeService: StoreRepositoryService,
    private jackpotTriggerDataService: StoreJackpotTriggerDataService,
    private syndicateService: SyndicateRepositoryService,
    private walletService: WalletService,
    private injector: Injector,
    private catchService: CatchErrorService,
    private RequestPaymentService: RequestPaymentService,
    private oneSignalService: OneSignalService,
    private RequestProfileService: RequestProfileService,
    private RequestUserService: RequestUserService,
    private userManagementService: UserManagementService,
    private gameBalanceService: GameBalanceService,
    private commonService: CommonService,
    private loggingService: LoggingService,
    private clarityService: ClarityService,
    private authStateService: AuthStateService,
  ) {
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l ? l : CommonVariable?.preferredLanguage;
        if (l) {
          // translate.use(l ? l : CommonVariable?.preferredLanguage);
        }
      },
      (err) => {
        return err;
      }
    );
  }

  updateSocialConnect(isConnected: boolean) {
    const userId = this.authService.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    if (userId == null) return;
    // Modular Firestore API for updating the document
    const path_id = "yzF1Ss5eiMqnh91zQMtv";
    const doc_data = { social_account_connected: isConnected };
    const method = "update";

    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      method,
    }).subscribe({
      error: (error) => {
        return error;
      }
    });
  }

  hideAutoRefreshInboxMessage(limb_id: number) {
    this.authService.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    if (!limb_id) {
      throw new Error("Invalid limb_id. Operation cannot proceed.");
    }
    localStorage.setItem("hideMessage", "true");
    const path_id = "dQ2JLcXGOr5fbVp8gyIf";
    const doc_data = {
      hide: true,
      dismissed: true,
    };
    const dv = { docId: limb_id };
    const method = "update";

    return firstValueFrom(this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      dv,
      method,
    })).catch((err) => {
      return err;
    });
  }

  updateUserBalalnce(credit: number) {
    const path_id = "SK8mBmn1R6bNDdj1cul8";
    const doc_data = { preferred_currency: { credit: credit } };
    const method = "update";
    return lastValueFrom(
      this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      })
    ).catch((err) => {
      console.error('Error updating preferred currency:', err);
      throw err;
    });
  }

  checkAutoRefresh() {
    return this.commonService.getUid("checkAutoRefresh").pipe( // ✅ replaced authService with commonService
      switchMap((userId) => {
        if (!userId) {
          // Return an observable with null or default if no userId
          return of(null);
        }

        const docRef = doc(this.firestore, `users/${userId}/in_app_notification/status`);

        // getDoc returns a Promise, so wrap it with from() to convert to Observable
        return from(getDoc(docRef)).pipe(
          map(snapshot => snapshot.exists() ? snapshot.data() : null)
        );
      })
    );
  }


  checkStoreCards() {
    let walletServiceRef = this.walletService;
    if (!Object.keys(this.walletService)?.length) {
      walletServiceRef = this.injector.get(WalletService);
    }
    walletServiceRef.paymentDetails$
      .pipe(
        take(1),
        map((paymentDetails: Data) => {
          const storedCards = walletServiceRef.getFilteredWalletCards(paymentDetails);
          return storedCards;
        })
      )
      .subscribe((cards) => {
        if (cards?.length > 0) {
          this.gameBalanceService?.isCreditNotify$?.subscribe((notify) => {
            if (notify === false) {
              this.authService.sendCreditRefreshInboxMessage().subscribe();
            }
          });
        } else {
          this.checkAutoRefresh().subscribe((creditRefresh: Data) => {
            if (creditRefresh?.credit_refresh?.status) {
              from(this.turnOffCreditRefresh())
                .pipe(
                  catchError((error) => {
                    return error;
                  })
                )
                .subscribe(() => {
                  /* const msg = this.translatedToastMsg?.["1027"]?.[this.selectedLanguage];
                this.authService.showToast(msg ? msg : "Code Error", "Success", ""); */
                  this.authService.getConstantsMsg("1027").then((translatedMsg) => {
                    const defaultMsg = {
                      message: {
                        line_1: "Auto refresh credit has been disabled,",
                        line_2: "since there is no active card available",
                      },
                      style: {
                        type: "Success",
                        icon: "tick circle success.png",
                      },
                    };
                    this.authService.showToastMessage(translatedMsg, defaultMsg);
                  });
                });
            }
          });
        }
      });
  }

  getMarkupContent(markupContentType: string) {
    const docRef = doc(this.firestore, `Global_lookup/${markupContentType}`);
    return docData(docRef);
    // docData returns Observable of the document data directly
  }

  getMGM() {
    const docRef = doc(this.firestore, `MGM/h6UhAn9HuwwGFIkNowKZ`);
    return docData(docRef).pipe(
      // The JSON.parse(JSON.stringify(...)) is often used to deep clone.
      // If you need a deep clone here, keep it; otherwise, just return the data.
      map(data => JSON.parse(JSON.stringify(data)))
    );
  }

  getWPContent(content: string): Observable<string | undefined> {
    const path = `general_modules/${content}`;
    const docRef = doc(this.firestore, path);

    return new Observable<DocumentData>((subscriber) => {
      const unsubscribe = onSnapshot(
        docRef,
        (snapshot) => subscriber.next(snapshot),
        (error) => subscriber.error(error)
      );
      return () => unsubscribe();
    }).pipe(
      map((snapshot) => {
        try {
          const res = snapshot?.data() as any;
          if (!res || !res.content?.length) return;

          const zeroNumber = 0;
          const firstContent = res.content[zeroNumber];

          if (content === 'privacy_policy') {
            return firstContent?._content?.[this.selectedLanguage];
          }

          if ('int_content' in firstContent) {
            const intContent = firstContent.int_content?.[this.selectedLanguage];
            return intContent
              || firstContent?._content?.[this.selectedLanguage]
              || firstContent?.content;
          } else {
            return " ";
          }
        } catch (err) {
          this.loggingService.logError("Error processing WP content:", err);
          return undefined;
        }
      })
    );
  }
  updateUserMSN(msn: string) {
      // const api = this.angularFireFunctions.httpsCallable("api");
      // return api(this.requestBodyService.getUpdateMSNRequest(msn?.substring(1)));
      return this.RequestUserService.getUpdateMSNRequest(msn?.substring(1)).pipe(
      switchMap((reqBody) =>{
        return this.authService?.getV2CloudFunction("apiv2",reqBody);
      }
      )
    );
  }

  async turnOffCreditRefresh() {
    // Update promo_details in Firestore
    const path_id1 = "1cblQl68QltRugb1YDAY";
    const method1 = "read";

    this.authService?.getV2CloudFunction("updatefirestoredoc", {
      path_id: path_id1,
      method: method1,
    }).pipe(
      map((snapshot: any) => snapshot?.data)
    ).subscribe({
      next: (docSnap: any) => {
        if (docSnap) {
          const promoDetails = docSnap?.promo_details || [];
          const updatedPromoDetails = promoDetails.map(promo => promo.promo_name === "activate_auto_topup" ? { ...promo, status: false } : promo);
          this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id: "1cblQl68QltRugb1YDAY",
            doc_data: { promo_details: updatedPromoDetails },
            method: "update",
          }).pipe(catchError((error) => of(error))).subscribe();
        }
      },
      error: (error: any) => {
        console.error("Error fetching promo details:", error);
      }
    });
    const path_id = "KdpTQsnyjKvgqvjItVvT";
    const doc_data = { credit_refresh: { status: false, updatedOn: new Date() } };
    const method = "update";

    return this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      method,
    });
  }

  turnOnCreditRefresh(isFirst?: boolean) {
    const userId = this.authService.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    if (!userId) return;
    return new Promise((resolve, reject) => {
      let dataToUpdate = { credit_refresh: { status: true, low_trigger_threshold: 1, updatedOn: new Date() } } as Data;
      this.checkAutoRefresh().subscribe(
        async (creditRefresh: Data) => {
          if (isFirst) {
            if (creditRefresh?.out_of_points_promotion) {
              dataToUpdate["credit_refresh"].isFirst = false;
              dataToUpdate["out_of_points_promotion"] = { updatedOn: new Date() };
            } else {
              dataToUpdate["credit_refresh"].isFirst = false;
            }
          } else {
            if (creditRefresh?.out_of_points_promotion) {
              dataToUpdate["out_of_points_promotion"] = { updatedOn: new Date() };
            }
          }

          const path_id = "KdpTQsnyjKvgqvjItVvT";
          const doc_data = { dataToUpdate };
          const method = "update";

          this.authService.getV2CloudFunction("updatefirestoredoc", {
            path_id,
            doc_data,
            method,
          }).subscribe({
            next: () => {
              this.clarityService.event("auto_topup_activated");
            },
            error: (err) => {
              console.error('Error in updatefirestoredoc', err);
              throw err;
            }
          });
        },
        (error) => reject(error)
      );

      // Update promo_details in Firestore
      const path_id = "1cblQl68QltRugb1YDAY";
      const method = "read";

      this.authService?.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        method,
      }).pipe(
        map((snapshot: any) => snapshot?.data)
      ).subscribe({
        next: (docSnap: any) => {
          if (docSnap) {
            const promoDetails = docSnap?.promo_details || [];
            const updatedPromoDetails = promoDetails.map(promo => promo.promo_name === "activate_auto_topup" ? { ...promo, status: true } : promo);
            this.authService.getV2CloudFunction("updatefirestoredoc", {
              path_id: "1cblQl68QltRugb1YDAY",
              doc_data: { promo_details: updatedPromoDetails },
              method: "update",
            }).pipe(catchError((error) => of(error))).subscribe();
          }
        },
        error: (error: any) => {
          console.error("Error fetching promo details:", error);
        }
      });
    });
  }

  // This method is used to get share link
getShareLink(): Promise<string> {
  const userId = this.authService.getUid$("AccountRepositoryService").pipe(
    switchMap((userId) => {
      return of(userId) || of(null);
    })
  );

  return new Promise((resolve, reject) => {
    this.authService?.userDetails$?.subscribe(
      async (userDetails: any) => {
        try {
          if (userDetails && userDetails.referral_url) {
            resolve(userDetails.referral_url);
          } else {
            if (!userDetails || !userDetails.referral_code) {
              reject(new Error("User details or referral code missing"));
              return;
            }

            // Using cloud function to create short link
            this.authService
              ?.getV2CloudFunction("callshortlinkapiv2", { referral_code: "None" })
              .pipe(
                retry(3),
                catchError((error) => {
                  return throwError(error);
                })
              )
              .subscribe({
                next: async (res: string) => {
                  try {
                    const shortUrl = res; // Store Referral URL
                    const mgmDocRef = doc(this.firestore, "urls/mgm");

                    // 🟩 Block 1
                    {
                      const path_id = "N5NxHkhc573iuoSwymIT";
                      const doc_data = { shortUrl: shortUrl };
                      const method = "update";

                      this.authService.getV2CloudFunction("updatefirestoredoc", {
                        path_id,
                        doc_data,
                        method,
                      }).subscribe({
                        error: (err) => {
                          console.error("Error in updatefirestoredoc", err);
                          throw err;
                        },
                      });
                    }

                    if (!userId) {
                      reject(new Error("User ID missing in localStorage"));
                      return;
                    }

                    // 🟩 Block 2
                    {
                      const path_id = "N5NxHkhc573iuoSwymIT";
                      const doc_data = {
                        url: `${document.location.origin}?custRefId=${userId}&ref=${userDetails.referral_code}`,
                      };
                      const method = "update";

                      this.authService.getV2CloudFunction("updatefirestoredoc", {
                        path_id,
                        doc_data,
                        method,
                      }).subscribe({
                        error: (err) => {
                          console.error("Error in updatefirestoredoc", err);
                          throw err;
                        },
                      });

                      this.authService.getV2CloudFunction("updatefirestoredoc", {
                        path_id,
                        doc_data,
                        method,
                      }).subscribe({
                        error: (err) => {
                          console.error("Error in updatefirestoredoc", err);
                          throw err;
                        },
                      });
                    }

                    // Fetch updated mgm link
                    const mgmSnapshot = await getDoc(mgmDocRef);

                    if (!mgmSnapshot.exists()) {
                      reject(new Error("MGM document not found"));
                      return;
                    }

                    const mgmLink = mgmSnapshot.data();
                    if (!mgmLink?.shortUrl) {
                      reject(new Error("shortUrl not found in MGM document"));
                      return;
                    }

                    // 🟩 Block 3
                    {
                      const path_id = "yzF1Ss5eiMqnh91zQMtv";
                      const doc_data = { referral_url: mgmLink.shortUrl };
                      const method = "update";

                      await this.authService.getV2CloudFunction("updatefirestoredoc", {
                        path_id,
                        doc_data,
                        method,
                      }).subscribe({
                        error: (error) => {
                          console.error("Error updating MGM link:", error);
                        },
                      });
                    }

                    resolve(mgmLink.shortUrl);
                  } catch (error) {
                    reject(error);
                  }
                },
                error: (err) => {
                  reject(err);
                },
              });
          }
        } catch (err) {
          reject(err);
        }
      },
      (err) => {
        reject(err);
      }
    );
  });
}



  helpAndFaq(): Observable<any> {
    const docRef = doc(this.firestore, '/general_modules/help-faq/FAQ_Phases/awareness');

    // Gate on customTokenReady$ before opening the onSnapshot listener to prevent
    // the permission-denied race condition on iOS/Safari (LOTTOSOCIAL-PWA-1MEE).
    return this.authStateService.customTokenReady$.pipe(
      take(1),
      switchMap(() => new Observable<DocumentData>(subscriber => {
        const unsubscribe = onSnapshot(docRef,
          (snapshot) => subscriber.next(snapshot),
          (error) => subscriber.error(error)
        );

        return () => unsubscribe();
      }).pipe(
      map((snapshot) => {
        const helpData: any = snapshot?.data();
        if (!helpData) return;

        helpData["things_to_know_List"] = [];
        helpData["answerList"] = [];

        Object.keys(helpData.things_to_know || {})
          .slice(0, Object.keys(helpData.things_to_know).length - 1)
          .forEach((key) => {
            helpData.things_to_know_List.push(helpData.things_to_know[key]);
          });

        Object.keys(helpData.answers || {})
          .slice(0, Object.keys(helpData.answers).length - 1)
          .forEach((key) => {
            helpData.answerList.push(helpData.answers[key]); // FIXED: was using wrong source
          });

        return helpData;
      })
    ))
    );
  }

  disableUser() {
    const userId = this.authService.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    return this.authService.getV2CloudFunction("disableuserv2", userId);

  }

  updateAccountDetails(profileDataControls, expectedUserId?: string) {
    return this.RequestProfileService.getUserProfileUpdateRequest(profileDataControls, expectedUserId).pipe(
      take(1),
      switchMap((reqBody) => {
        if (!reqBody) {
          this.authService.errorHandlerLogs('[AccountRepositoryService] Request body is null, cannot update account details');
          return of(null);
        }
        if (!expectedUserId) return this.authService.getV2CloudFunction("apivalidateasyncv2", reqBody);
        const modules = (reqBody as Data).modules;
        const beforeInvoke = () => getAuth().currentUser?.uid === expectedUserId &&
          !getAuth().currentUser?.isAnonymous && Array.isArray(modules) && modules.length > 0 &&
          modules.every(module => module.user_id === expectedUserId);
        return this.authService.getV2CloudFunction("apivalidateasyncv2", reqBody, { retryAttempts: 0, beforeInvoke });
      })
    );
  }


  userFacebookProfileUpdate(profileDataControls) {
    return this.authService.getV2CloudFunction(
      "apivalidateasyncv2",
      this.RequestProfileService?.userFacebookProfileUpdate(profileDataControls)
    );
  }

  updateProfileImageFromFacebook(imageUrl: string) {
    return this.authService?.getV2CloudFunction("apiv2", this.RequestProfileService.updateProfileImageFromFacebook(imageUrl));
  }

  updateProfileImage(imageUrl) {
    // const api = this.angularFireFunctions.httpsCallable("api");
    // return api(this.requestBodyService.updateProfileImage(imageUrl));
    return this.authService?.getV2CloudFunction("apiv2", this.RequestProfileService.updateProfileImage(imageUrl));
  }

  fetchGenderFromFacebook(idToken: string): Observable<Data> {
    const url = `https://graph.facebook.com/v19.0/me?fields=name,gender&access_token=${idToken}`
    return this.http.get(url, { observe: 'body' }).pipe(
      map((res: Data) => {
        return res
      }),
      catchError(err => throwError(err))
    );
  }

  getUserPreferences(): Observable<any> {
    const docRef = doc(this.firestore, 'general_modules/preference');

    return from(getDoc(docRef)).pipe(
      map((docSnap) => {
        if (!docSnap.exists()) {
          return null;
        }
        const data = docSnap.data();
        if (!data) {
          console.warn('[getUserPreferences] No preference data found in document.');
        }
        return data;
      }),
      catchError((err) => {
        console.error('[getUserPreferences] Failed to fetch preferences:', err);
        return of(null); // You can return a fallback object here if needed
      })
    );
  }


  getUserSpecificPreferences(): Observable<any> {
    return this.authService.getUid$("HomeRepositoryService").pipe(
      switchMap((userId) => {
        if (!userId) throw new Error('User ID not found in localStorage');
        const prefDocRef = doc(this.firestore, `users/${userId}/preference/options`);

        // Cloud-function writes do not update the local Firestore cache. docData()
        // + take(1) therefore returned the pre-edit snapshot and the Preferences
        // toggles appeared inverted after leaving and returning.
        return from(getDocFromServer(prefDocRef)).pipe(
          map((snapshot) => (snapshot.exists() ? snapshot.data() : null)),
          catchError(() =>
            from(getDoc(prefDocRef)).pipe(
              map((snapshot) => (snapshot.exists() ? snapshot.data() : null))
            )
          ),
          catchError((error) => {
            console.error('Error fetching user preferences:', error);
            return of(null);
          })
        );
      }))
  }

  saveUserSpecificPreferences(updatePrefInfo: Data): Observable<Data> {
    return this.authService.getUid$("AccountRepositoryService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) {
          return throwError(() => new Error('User ID not found'));
        }
        const prefDocRef = doc(this.firestore, `users/${userId}/preference/options`);
        return from(setDoc(prefDocRef, updatePrefInfo, { merge: true })).pipe(
          map(() => updatePrefInfo),
          catchError((error) => {
            this.authService.errorHandlerLogs('Error saving preferences:', error);
            return this.authService.getV2CloudFunction("updatefirestoredoc", {
              path_id: `oRGR7JEhALug9dTYwIZE`,
              doc_data: updatePrefInfo,
              method: "update",
            });
          })
        );
      })
    );
  }

  getOneSignalDetailsOption() {
    const userId = this.authService.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    if (!userId) {
      return throwError(() => new Error("User is not authorized!"));
    }

    const docRef = doc(this.firestore, `users/${userId}/onesignal-push-notifications/details`);

    return from(getDoc(docRef)).pipe(
      map(snapshot => snapshot.exists() ? snapshot.data() : null),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * The function `saveOneSignalDetailsOption` updates the subscription status for a specific device ID
   * in a list of token details retrieved from an API call.
   */
  saveOneSignalDetailsOption(subscription_status: boolean): void {
    const deviceId = CommonVariable?.FingerprintJSConst || localStorage?.getItem("fingerSalt");
    this.getOneSignalDetailsOption().subscribe({
      next: (data) => {
        if (!data) {
          return;
        }
        data?.token_details?.forEach((element) => {
          const isMatchingFingerprint = element?.fingerprint_id === deviceId;
          const isMatchingOnesignal = element?.onesignal_id === deviceId;

          if (isMatchingFingerprint || isMatchingOnesignal) {
            element.subscription_status = subscription_status ?? true;

            if (isMatchingOnesignal) {
              element.fingerprint_id = deviceId;
              element.browser = this.authService?.getBrowserVersion() || "N/A";
            }
          }
        });
        const oneSignalData = {
          token_status: 0,
        }
        const useExistingData = true;
        this.oneSignalService?.updateOneSignalToken(oneSignalData, useExistingData);
        this.setOneSignalPushNotification(data);
      },
      error: (error) => {
        return error;
      },
    });
  }

  /**
   * The function `setOneSignalPushNotification` sets the push notification token details for a user in a
   * Firestore database.
   */
  setOneSignalPushNotification(tokenDetails: any) {
    const userId = this.authService.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    if (!userId || !tokenDetails) {
      return throwError(new Error("User is not authorized!"));
    }
    // Modular Firestore API: Reference the Firestore document

    const path_id = "f4r0yr15mqEsGYPl0EHr";
    const doc_data = tokenDetails;
    const method = "update";

    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      method,
    }).subscribe({
      error: (err) => {
        console.error("Error updating pushNotificationDocRef document:", err);
        throw err;
      }
    });
  }

  updateUserPreferences(smsPref: boolean, emailPref: boolean) {
    try {
      // V2
      return this.authService.getV2CloudFunction(
        "postasynceventsv2",
        this.RequestUserService?.getUpdateUserPreferencesRequest(smsPref, emailPref)
      );
      // V1
      /* const api = this.angularFireFunctions.httpsCallable("postAsyncEvents");
      return api(this.requestBodyService.getUpdateUserPreferencesRequest(smsPref, emailPref)); */
    } catch (error) {
      return error;
    }
  }

  insertUserGDPR(requestOption: string, moduleName: string) {
    // const api = this.angularFireFunctions.httpsCallable("api");
    // return api(this.requestBodyService.getInsertUserGdpr(requestOption, moduleName));
    return this.RequestUserService.getInsertUserGdpr(requestOption, moduleName).pipe(
      filter((reqBody) => !!reqBody),
      switchMap((reqBody) =>
        this.authService.getV2CloudFunction("apiv2", reqBody)
      )
    );
  }

  public updateGDPRUserPreferences(
    smsPref: boolean,
    emailPref: boolean,
    pushPref: boolean
  ): Observable<any> {
    return this.RequestPaymentService.updatePreferencesGDPRRequest(smsPref, emailPref, pushPref).pipe(
      filter((req) => req !== null),
      switchMap((req) =>
        this.authService.getV2CloudFunction("postasynceventsv2", req)
      )
    );
  }

  updateUserNickName(nickName) {
    // const api = this.angularFireFunctions.httpsCallable("api");
    // return api(this.requestBodyService.getUserNicknameUpdateRequest(nickName));
    return this.authService?.getV2CloudFunction(
      "apiv2",
      this.RequestProfileService.getUserNicknameUpdateRequest(nickName)
    );
  }

  removeWalletCard(cardId: string): Observable<any> {
    return this.RequestPaymentService.removeWalletCard(cardId).pipe(
      filter((req) => req !== null), // don’t send invalid requests
      switchMap((req) =>
        this.authService.getV2CloudFunction("apiv2", req)
      )
    );
  }


  myOnGoingSyndicates$ = this.userManagementService.userRequestForHome$.pipe(
    switchMap(() => {
      return this.authService.getUid$("AccountRepositoryService").pipe(
        filter((userId) => !!userId),
        switchMap((userId) => {
          // Modular Firestore API: Reference the collection and apply filters
          const syndicateListRef = collection(this.firestore, `users/${userId}/userSyndicate_list`);
          const syndicateListQuery = query(
            syndicateListRef,
            where("current_status", "in", [ActiveStatus.Running, ActiveStatus.Paused, ActiveStatus.Holiday])
          );

          // Gate on customTokenReady$ before opening the onSnapshot listener to prevent
          // the permission-denied race condition on iOS/Safari (LOTTOSOCIAL-PWA-1MEE)
          // where the listener fires before the Firebase custom token is stored.
          return this.authStateService.customTokenReady$.pipe(
            take(1),
            switchMap(() => new Observable<any[]>((subscriber) => {
              const unsubscribe = onSnapshot(
                syndicateListQuery,
                (snapshot) => {
                  const actions = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                  }));
                  subscriber.next(actions);
                },
                (error) => {
                  this.authService.errorHandlerLogs("Error fetching syndicates:", error)
                  // For permission-denied errors (e.g. during iOS auth token refresh),
                  // emit an empty array instead of propagating the error. Calling
                  // subscriber.error() triggers RxJS teardown which calls unsubscribe()
                  // while Firestore is mid-error-processing, causing an internal
                  // assertion failure (ID: ca9/b815) surfaced as UnsubscriptionError.
                  if ((error as Data)?.code === 'permission-denied') {
                    subscriber.next([]);
                  } else {
                    subscriber.error(error);
                  }
                }
              );

              return () => {
                try {
                  unsubscribe();
                } catch (e) {
                  // Swallow any Firestore internal errors thrown during teardown
                  // to prevent them from surfacing as RxJS UnsubscriptionError.
                  this.authService.errorHandlerLogs("Error during Firestore listener teardown:", e);
                }
              };
            }).pipe(
              map((res: any) => {
                if (!res || !Array.isArray(res)) return [];
                return res.filter(
                  (f) =>
                    f.syndicate_type === "Ongoing" &&
                    (f.cardType === "Managed" || (f.cardType === "Private" && f.status !== "paused" && f.hide === false))
                );
              }),
              shareReplay(1)
            ))
          );
        }),
        startWith([])
      );
    })
  );
  private snapShotData(actions: import("@angular/fire/compat/firestore").DocumentChangeAction < unknown >[]): any[] {
    return actions.map((a) => {
      const data = (a?.payload.doc?.data() as Data) ?? {};
      const id = a?.payload.doc.id;
      return { id, ...data };
    });
  }

  myJackpots$ = this.jackpotTriggerDataService.jackpotTrigger$;

  leaveSyndicate(syndicate) {
    return this.syndicateService.leaveSyndicate(syndicate.id, syndicate.cardType, "");
  }

  leaveJackpot(jackpot) {
    return this.syndicateService?.deactivateTrigger(jackpot);
  }
  updateQRCode(base64: string) {
    const userId = this.authService.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    if (!userId) return; // Guard clause for invalid userId
    // Modular Firestore API: Reference the Firestore document
    const path_id = "yzF1Ss5eiMqnh91zQMtv";
    const doc_data = { QRCode: base64 };
    const method = "update";

    return lastValueFrom(
      this.authService.getV2CloudFunction("updatefirestoredoc", {
        path_id,
        doc_data,
        method,
      })
    ).catch((err) => {
      console.error('Error updating preferred currency:', err);
      throw err;
    });
  }

  // This method has been Use to get Short referral URL
  getShortReferralUrl(referral_code, syndicate_promotion, gc = "") {
    const apiFunctionName = "callshortlinkapiv2"; // Assign the function name to a variable
    try {
      // Call the "callshortlinkapiv2" function from AngularFireFunctions with the referral code
      // const createShortLink = this.angularFireFunctions.httpsCallable(apiFunctionName)({ referral_code: referral_code });
      const createShortLink = this.authService?.getV2CloudFunction(apiFunctionName, {
        referral_code: referral_code,
        call_from: syndicate_promotion,
        gc,
      });
      return createShortLink.pipe(
        retry(3),
        catchError((error) => {
          return throwError(() => error);
        })
      );
    } catch (error) {
      throw new Error(`Error creating short link: ${error}`);
    }
  }
  // This method has been use to set short referral url in the user details
  setShortUrl(shortURL: string) {
    const userId = this.authService.getUid$("AccountRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    if (!userId) return; // Guard clause for invalid userId
    // Modular Firestore API: Reference the Firestore document
    const path_id = "yzF1Ss5eiMqnh91zQMtv";
    const doc_data = { referral_url: shortURL };
    const method = "update";

    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      method,
    }).subscribe({
      error: (error) => {
        console.error("Error updating MGM link:", error);
      }
    });
  }

  updatePrimaryCard(cardId: string) {
    try {
      return this.RequestPaymentService.updatePrimaryCard(cardId).pipe(
        switchMap((payload) => {
          return this.authService.getV2CloudFunction("updateprimarycard", payload);
        })
      );
    } catch (error) {
      return throwError(() => error);
    }
  }


  public dismissAll(page: string): void {
    this.dismissAllSubject.next(page);
  }

  updateProfileImageModerator(imageUrl: string) {
    try {
      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      const api = httpsCallable(functions, "userimageuploadv2");

      // ✅ Use switchMap to wait for payload before calling API
      return this.RequestProfileService.updateProfileImageModerator(imageUrl).pipe(
        switchMap((payload) => {
          if (!payload) {
            return of(null);
          }
          return from(api(payload)).pipe(
            map((res: any) => (res?.data ? res.data : res))
          );
        })
      );
    } catch (error) {
      return throwError(() => error);
    }
  }


  updateCoverImageModerator(imageUrl: string) {
    const authService = this.injector.get(AuthService);
    const functions = getFunctions();
    functions.region = AuthService.FIREBASE_REGION;
    const api = httpsCallable(functions, "userimageuploadv2");

    return authService.getUid$("RequestProfileService").pipe(
      switchMap((userId) => {
        if (!userId) {
          console.error("[updateCoverImageModerator] ❌ No userId found.");
          return of(null);
        }

        // ✅ Build payload synchronously (not returning an observable here)
        const payload = {
          api_response: {},
          img_url: imageUrl,
          process: false,
          purpose: {
            content: {
              profile_picture: imageUrl,
            },
            destination_document: `/users/${userId}`,
            type: "cover_picture",
          },
          userid: userId,
        };
        return from(api(payload)).pipe(
          map((res: any) => {
            return res?.data ?? res;
          }),
          catchError((error) => {
            return of(null);
          })
        );
      })
    );
  }


  getServiceWidget(id: string): Observable<DocumentData | undefined> {
    if (!id) return of([]); // Guard clause for invalid `id`
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) {
          return new Observable<DocumentData | undefined>((subscriber) => subscriber.next(undefined)); // Return undefined if offline
        }
        // Modular Firestore API: Reference the document
        const widgetDocRef = doc(this.firestore, `users/${id}/service_value_widget/details`);
        // Gate on customTokenReady$ before opening the onSnapshot listener so it
        // never fires before the Firebase custom token is confirmed stored,
        // preventing the permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
        return this.authStateService.customTokenReady$.pipe(
          take(1),
          switchMap(() => new Observable<DocumentData | undefined>((subscriber) => {
            const unsubscribe = onSnapshot(
              widgetDocRef,
              (snapshot) => {
                if (snapshot.exists()) {
                  subscriber.next(snapshot.data());
                } else {
                  subscriber.next(undefined);
                }
              },
              (error) => {
                console.error("Error fetching widget details:", error);
                subscriber.error(error);
              }
            );

            // Cleanup function to unsubscribe from Firestore listener
            return () => unsubscribe();
          }))
        );
      }),
      shareReplay(1)
    );
  }

  updateProfilePicture(path: string) {
    return this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id: "yzF1Ss5eiMqnh91zQMtv",
      doc_data: { profile_picture: path },
      method: "update",
    })
  }
}
